import React, { useState, useEffect, useCallback, useRef } from 'react';
import gateApi from '../api';
import { useAuth } from '../context/AuthContext';
import { STATUS_CONFIG } from '../constants';
import {
  C, Field, TextInput, Select, TextArea, SubmitButton, Banner,
} from './FormKit';

const CORRECTION_REASONS = [
  { code: '', label: '— select a reason —' },
  { code: 'TYPO', label: 'Typo / misread scale' },
  { code: 'WRONG_VEHICLE', label: 'Weighed under the wrong vehicle' },
  { code: 'SCALE_ERROR', label: 'Weighbridge / scale malfunction' },
  { code: 'OTHER', label: 'Other (explain below)' },
];

// Narrow, single-purpose station: search a plate, weigh it. Handles both
// visits -- tare (empty, 1st visit) and gross (loaded, 2nd visit) -- picking
// the right field based on the selected vehicle's current status.
export default function WeighbridgeForm() {
  const { user, logout } = useAuth();
  const [mode, setMode] = useState('weigh'); // 'weigh' | 'correct'

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 22px', borderBottom: `1px solid ${C.border}`, background: C.panel,
      }}>
        <span style={{
          fontFamily: C.mono, fontSize: 13, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: C.muted,
        }}>BSC Gate · Weighbridge</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 12, color: C.faint }}>{user?.fullName}</span>
          <button onClick={logout} style={{
            padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`,
            background: '#fff', color: C.muted, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '28px 22px 80px' }}>
        {/* Mode toggle */}
        <div style={{
          display: 'flex', gap: 6, marginBottom: 22, background: C.panel2,
          border: `1px solid ${C.border}`, borderRadius: 12, padding: 4,
        }}>
          {[{ key: 'weigh', label: 'Weigh In' }, { key: 'correct', label: 'Correct a Weight' }].map(t => (
            <button key={t.key} onClick={() => setMode(t.key)} style={{
              flex: 1, padding: '10px 0', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: mode === t.key ? C.panel : 'transparent',
              color: mode === t.key ? C.text : C.muted,
              fontFamily: C.mono, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
              boxShadow: mode === t.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>{t.label.toUpperCase()}</button>
          ))}
        </div>

        {mode === 'weigh' ? <WeighIn /> : <CorrectWeight />}
      </div>
    </div>
  );
}

// ── Weigh In — the normal forward flow (tare, then gross) ───────────────────
function WeighIn() {
  const [vehicles, setVehicles] = useState([]);
  const [selTx, setSelTx]       = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [weightInput, setWeightInput] = useState('');
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);

  const selected = vehicles.find(v => v.transactionId === selTx);
  const needsTare = selected?.lifecycleStatus === 'PENDING_TARE_WEIGHT';
  const needsGross = selected?.lifecycleStatus === 'PENDING_LOADING';

  const loadVehicles = useCallback(async () => {
    try { setVehicles(await gateApi.getWeighbridgePending()); }
    catch { /* keep last list if fetch fails */ }
  }, []);

  useEffect(() => {
    loadVehicles();
    const interval = setInterval(loadVehicles, 10000);
    return () => clearInterval(interval);
  }, [loadVehicles]);

  useEffect(() => {
    function onClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = vehicles.filter(v =>
    v.licensePlate?.toUpperCase().includes(searchQuery.toUpperCase().trim())
  );

  function selectVehicle(v) {
    setSelTx(v.transactionId);
    setSearchQuery(v.licensePlate || '');
    setShowDropdown(false);
    setWeightInput('');
    setResult(null);
  }

  function clearSelection() {
    setSelTx(''); setSearchQuery(''); setWeightInput('');
    setResult(null);
  }

  async function submit() {
    if (!selTx) { setResult({ ok: false, msg: 'Select a vehicle first.' }); return; }
    const weight = parseFloat(weightInput);
    if (!weight || weight <= 0) { setResult({ ok: false, msg: 'Enter a valid weight.' }); return; }

    setBusy(true); setResult(null);
    try {
      const r = needsTare
        ? await gateApi.recordTareWeight(selTx, weight)
        : await gateApi.recordGrossWeight(selTx, weight);
      setResult({
        ok: true,
        msg: needsTare
          ? `${r.plate} — tare weight recorded. Sent for loading.`
          : `${r.plate} — gross weight recorded. Net weight: ${r.netWeight} t. Sent for challan.`,
      });
      clearSelection();
      loadVehicles();
    } catch (e) {
      const d = e.response?.data;
      setResult({ ok: false, msg: d?.message || d?.error || 'Could not record weight.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      <Field label="Find vehicle"
             hint={vehicles.length ? `${vehicles.length} vehicle(s) waiting at the weighbridge` : 'No vehicles currently waiting'}>
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder={vehicles.length ? 'Type to search number plate…' : 'none waiting'}
            disabled={!vehicles.length}
            value={searchQuery}
            onFocus={() => setShowDropdown(true)}
            onChange={(e) => { setSearchQuery(e.target.value); setSelTx(''); }}
            style={{
              width: '100%', boxSizing: 'border-box', background: C.panel2,
              border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
              padding: '14px 16px', fontSize: 16, fontFamily: C.mono, outline: 'none',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
          />
          {showDropdown && vehicles.length > 0 && (
            <div style={{
              position: 'absolute', top: '102%', left: 0, right: 0,
              background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
              maxHeight: 260, overflowY: 'auto', zIndex: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: '6px 0',
            }}>
              {filtered.length > 0 ? filtered.map(v => (
                <div
                  key={v.transactionId}
                  onClick={() => selectVehicle(v)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', fontSize: 14,
                    fontFamily: C.sans, color: C.text, display: 'flex',
                    justifyContent: 'space-between', transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = C.panel2; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontFamily: C.mono, fontWeight: 700 }}>{v.licensePlate}</span>
                  <span style={{ color: C.muted, fontSize: 12 }}>
                    {v.lifecycleStatus === 'PENDING_TARE_WEIGHT' ? 'Needs tare weight' : 'Needs gross weight'}
                  </span>
                </div>
              )) : (
                <div style={{ padding: '14px 16px', color: C.faint, fontSize: 13, fontFamily: C.sans }}>
                  No matching vehicles found
                </div>
              )}
            </div>
          )}
        </div>
      </Field>

      {selected && (
        <div style={{
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: '12px 16px', marginBottom: 20, display: 'flex',
          gap: 24, flexWrap: 'wrap', fontSize: 13,
        }}>
          <span style={{ color: C.muted }}>Vehicle type:&nbsp;
            <span style={{ color: C.text }}>{selected.vehicleType || '—'}</span>
          </span>
          <span style={{ color: C.muted }}>Declared material:&nbsp;
            <span style={{ color: C.text }}>{selected.expectedLoad || '—'}</span>
          </span>
          {needsGross && (
            <span style={{ color: C.muted }}>Tare weight on record:&nbsp;
              <span style={{ color: C.green, fontFamily: C.mono, fontWeight: 700 }}>
                {selected.tareWeight != null ? `${selected.tareWeight} t` : '—'}
              </span>
            </span>
          )}
        </div>
      )}

      {selected && (
        <>
          <Field label={needsTare ? 'Tare weight (tonnes) — empty truck' : 'Gross weight (tonnes) — loaded truck'}>
            <TextInput value={weightInput} onChange={setWeightInput} type="number" mono
              placeholder={needsTare ? 'e.g. 8.5' : 'e.g. 24.2'} />
          </Field>

          <SubmitButton onClick={submit} disabled={busy} color={C.teal}>
            {busy ? 'Saving…' : needsTare ? 'Record Tare Weight' : 'Record Gross Weight'}
          </SubmitButton>

          <button onClick={clearSelection} style={{
            marginTop: 14, width: '100%', background: 'none', border: 'none',
            color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.08em',
          }}>CLEAR SELECTION</button>
        </>
      )}
    </>
  );
}

// ── Correct a Weight — fix a wrongly-entered tare/gross after the fact ─────
// Any vehicle that already has a weight on record and hasn't been billed yet
// is fair game. Once a Challan exists, only an admin can still push a fix
// through here — a plain weighbridge operator gets a clear "ask an admin"
// message instead of a failed request.
function CorrectWeight() {
  const [vehicles, setVehicles] = useState([]);
  const [selTx, setSelTx]       = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [tareInput, setTareInput]   = useState('');
  const [grossInput, setGrossInput] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);

  const selected = vehicles.find(v => v.transactionId === selTx);
  // Locked once money is committed to this weight — the challan is recorded OR
  // payment has been taken. Payment can land before any challan exists, so the
  // challan flag alone would leave paid trips editable.
  // Hard lock, no role exception: once a challan is recorded or payment taken,
  // the amount is frozen and the weight it was calculated from can't move.
  const locked = selected?.billingStarted;

  const loadVehicles = useCallback(async () => {
    try { setVehicles(await gateApi.getWeighbridgeCorrectable()); }
    catch { /* keep last list if fetch fails */ }
  }, []);

  useEffect(() => {
    loadVehicles();
    const interval = setInterval(loadVehicles, 15000);
    return () => clearInterval(interval);
  }, [loadVehicles]);

  useEffect(() => {
    function onClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = vehicles.filter(v =>
    v.licensePlate?.toUpperCase().includes(searchQuery.toUpperCase().trim())
  );

  function selectVehicle(v) {
    setSelTx(v.transactionId);
    setSearchQuery(v.licensePlate || '');
    setShowDropdown(false);
    setTareInput(v.tareWeight != null ? String(v.tareWeight) : '');
    setGrossInput(v.grossWeight != null ? String(v.grossWeight) : '');
    setReasonCode(''); setReasonText('');
    setResult(null);
  }

  function clearSelection() {
    setSelTx(''); setSearchQuery('');
    setTareInput(''); setGrossInput('');
    setReasonCode(''); setReasonText('');
    setResult(null);
  }

  const tareChanged  = selected && selected.tareWeight != null && tareInput !== String(selected.tareWeight) && tareInput !== '';
  const grossChanged = selected && selected.grossWeight != null && grossInput !== String(selected.grossWeight) && grossInput !== '';

  async function submit() {
    if (!selTx) { setResult({ ok: false, msg: 'Select a vehicle first.' }); return; }
    if (!tareChanged && !grossChanged) { setResult({ ok: false, msg: 'Change the tare or gross weight before saving.' }); return; }
    if (!reasonCode) { setResult({ ok: false, msg: 'Pick a reason for the correction.' }); return; }
    if (reasonCode === 'OTHER' && !reasonText.trim()) {
      setResult({ ok: false, msg: 'Explain the reason in the text box.' }); return;
    }

    setBusy(true); setResult(null);
    try {
      const fixed = [];
      if (tareChanged) {
        await gateApi.correctTareWeight(selTx, parseFloat(tareInput), reasonCode, reasonText);
        fixed.push('tare');
      }
      if (grossChanged) {
        await gateApi.correctGrossWeight(selTx, parseFloat(grossInput), reasonCode, reasonText);
        fixed.push('gross');
      }
      setResult({ ok: true, msg: `${selected.licensePlate} — corrected: ${fixed.join(' & ')} weight.` });
      clearSelection();
      loadVehicles();
    } catch (e) {
      const d = e.response?.data;
      setResult({ ok: false, msg: d?.message || d?.error || 'Could not save the correction.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {result && <Banner type={result.ok ? 'success' : 'error'}>{result.msg}</Banner>}

      <Field label="Find vehicle"
             hint={vehicles.length ? `${vehicles.length} vehicle(s) with a weight on record` : 'No correctable vehicles right now'}>
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder={vehicles.length ? 'Type to search number plate…' : 'none available'}
            disabled={!vehicles.length}
            value={searchQuery}
            onFocus={() => setShowDropdown(true)}
            onChange={(e) => { setSearchQuery(e.target.value); setSelTx(''); }}
            style={{
              width: '100%', boxSizing: 'border-box', background: C.panel2,
              border: `1px solid ${C.border}`, borderRadius: 10, color: C.text,
              padding: '14px 16px', fontSize: 16, fontFamily: C.mono, outline: 'none',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
          />
          {showDropdown && vehicles.length > 0 && (
            <div style={{
              position: 'absolute', top: '102%', left: 0, right: 0,
              background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
              maxHeight: 280, overflowY: 'auto', zIndex: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: '6px 0',
            }}>
              {filtered.length > 0 ? filtered.map(v => {
                const stage = STATUS_CONFIG[v.lifecycleStatus];
                return (
                  <div
                    key={v.transactionId}
                    onClick={() => selectVehicle(v)}
                    style={{
                      padding: '12px 16px', cursor: 'pointer', fontSize: 14,
                      fontFamily: C.sans, color: C.text, transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = C.panel2; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontFamily: C.mono, fontWeight: 700 }}>{v.licensePlate}</span>
                      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {v.billingStarted && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: '#9D174D', background: '#FCE4EC',
                            padding: '2px 8px', borderRadius: 20, letterSpacing: '0.03em',
                          }}>BILLED</span>
                        )}
                        {stage && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                            padding: '3px 9px', borderRadius: 20, letterSpacing: '0.03em',
                            background: stage.bg, color: stage.color,
                          }}>{stage.label}</span>
                        )}
                      </span>
                    </div>
                    <div style={{ color: C.muted, fontSize: 12, marginTop: 3 }}>
                      Tare: {v.tareWeight != null ? `${v.tareWeight} t` : '—'} · Gross: {v.grossWeight != null ? `${v.grossWeight} t` : '—'}
                    </div>
                  </div>
                );
              }) : (
                <div style={{ padding: '14px 16px', color: C.faint, fontSize: 13, fontFamily: C.sans }}>
                  No matching vehicles found
                </div>
              )}
            </div>
          )}
        </div>
      </Field>

      {selected && locked && (
        <>
          <Banner type="error">
            {selected.paymentReceived ? 'Payment has already been taken' : 'A challan is already recorded'} for {selected.licensePlate},
            so the weight it was billed on can no longer be changed. If the weight is wrong, reject the trip and re-enter the vehicle.
          </Banner>
          <button onClick={clearSelection} style={{
            width: '100%', background: 'none', border: 'none',
            color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.08em',
          }}>CANCEL</button>
        </>
      )}

      {selected && !locked && (
        <>
          <Field label="Tare weight (tonnes)" hint={selected.tareWeight == null ? 'Not weighed yet — nothing to correct' : undefined}>
            <TextInput value={tareInput} onChange={setTareInput} type="number" mono
              disabled={selected.tareWeight == null}
              placeholder={selected.tareWeight == null ? '—' : 'e.g. 8.5'} />
          </Field>

          <Field label="Gross weight (tonnes)" hint={selected.grossWeight == null ? 'Not weighed yet — nothing to correct' : undefined}>
            <TextInput value={grossInput} onChange={setGrossInput} type="number" mono
              disabled={selected.grossWeight == null}
              placeholder={selected.grossWeight == null ? '—' : 'e.g. 24.2'} />
          </Field>

          <div style={{ height: 1, background: C.border, margin: '6px 0 20px' }} />

          <Field label="Reason for correction (required)">
            <Select value={reasonCode} onChange={setReasonCode}
              options={CORRECTION_REASONS.map(r => ({ value: r.code, label: r.label }))} />
          </Field>

          {reasonCode === 'OTHER' && (
            <Field label="Explain">
              <TextArea value={reasonText} onChange={setReasonText} placeholder="What was wrong and why…" />
            </Field>
          )}

          <SubmitButton onClick={submit} disabled={busy} color={C.amber}>
            {busy ? 'Saving…' : 'Save correction'}
          </SubmitButton>

          <button onClick={clearSelection} style={{
            marginTop: 14, width: '100%', background: 'none', border: 'none',
            color: C.faint, fontFamily: C.mono, fontSize: 12, cursor: 'pointer',
            letterSpacing: '0.08em',
          }}>CANCEL</button>
        </>
      )}
    </>
  );
}
